import clsxm from "@/lib/clsxm";
import {useLayoutEffect, useRef, useState, type ComponentPropsWithoutRef, type FC, type ReactNode} from "react";
import {useTranslation} from "react-i18next";

const LINE_CLAMP_CLASS: Record<number, string> = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
};

const TruncatedTextWithMore: FC<{
  children: ReactNode;
  className?: string;
  icon?: ReactNode;
  lineClamp?: keyof typeof LINE_CLAMP_CLASS;
  moreClassName?: string;
  moreLabel?: string;
  moreProps?: ComponentPropsWithoutRef<"span">;
  onClick?: () => void;
  onMoreClick?: () => void;
}> = ({
  children,
  className,
  icon,
  lineClamp = 1,
  moreClassName,
  moreLabel = "more",
  moreProps,
  onClick,
  onMoreClick,
}) => {
  const {t} = useTranslation("common");
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (el) setIsTruncated(lineClamp ? el.scrollHeight > el.clientHeight : el.scrollWidth > el.clientWidth);
  }, [children, lineClamp]);

  return (
    <div
      className={clsxm("flex items-center gap-1", className)}
      onClick={e => {
        if (onClick) {
          e.stopPropagation();
          onClick();
        }
      }}>
      {icon}
      <span ref={textRef} className={clsxm("min-w-0 flex-1", lineClamp && LINE_CLAMP_CLASS[lineClamp])}>
        {children}
      </span>
      {isTruncated && (
        <span
          {...moreProps}
          className={clsxm(
            "flex h-full shrink-0 cursor-pointer items-end font-semibold uppercase underline",
            moreClassName,
          )}
          onClick={e => {
            e.stopPropagation();
            if (onMoreClick) {
              onMoreClick();
            } else {
              onClick?.();
            }
          }}>
          {t(moreLabel as "more")}
        </span>
      )}
    </div>
  );
};

export default TruncatedTextWithMore;
